import * as React from "react";
import { makeStyles, createStyles, Theme, Grid, Typography } from "@material-ui/core";
import ReactMarkdown from "react-markdown";
import Section from "../Section";
import CustomLink from "../CustomLink";
import { Locale } from "../../utils";

import {
  SECTION_TINT,
  SECTION_WHITE,
  TEXT_BODY,
  TEXT_HEADING,
  SECTION_PADDING_Y,
} from "../../styles/homepageSections";

export type ContentBlockData = {
  subheadline?: string | null;
  headline?: string | null;
  description?: string | null;
};

export type PersonaQuestionData = {
  role: string;
  question: string;
  solutionLink?: {
    text: string;
    link: string;
    anchor?: string;
  } | null;
};

export type PersonaQuestionsData = {
  subheadline?: string | null;
  headline?: string | null;
  questions?: PersonaQuestionData[] | null;
};

export type ProblemDefinitionSectionProps = {
  locale: Locale;
  businessProblem?: ContentBlockData | null;
  personaQuestions?: PersonaQuestionsData | null;
};

export type HowWeSolveSectionProps = {
  howWeSolve?: ContentBlockData | null;
};

const hasText = (value?: string | null) =>
  Boolean(value && value.replace(/<[^>]*>/g, "").trim());

const markdownComponents = {
  p: ({ children }: { children?: React.ReactNode }) => (
    <p style={{ margin: "0 0 1em" }}>{children}</p>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong>{children}</strong>
  ),
};

const useStyles = makeStyles((t: Theme) =>
  createStyles({
    section: {
      padding: SECTION_PADDING_Y.mobile,
      [t.breakpoints.up("md")]: {
        padding: SECTION_PADDING_Y.desktop,
      },
    },
    blocks: {
      display: "flex",
      flexDirection: "column",
      gap: "5rem",
      [t.breakpoints.up("md")]: {
        gap: "7rem",
      },
    },
    centered: {
      textAlign: "center",
      maxWidth: 900,
      margin: "0 auto",
    },
  })
);

const useLabelStyles = makeStyles((t: Theme) =>
  createStyles({
    subheadline: {
      color: t.palette.primary.main,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      marginBottom: 12,
      [t.breakpoints.up("md")]: {
        fontSize: 13,
        marginBottom: 16,
      },
    },
    headline: {
      color: TEXT_HEADING,
      fontWeight: 600,
      fontSize: 32,
      lineHeight: 1.25,
      margin: 0,
      padding: "0 0 2rem !important",
      [t.breakpoints.up("md")]: {
        fontSize: 44,
        lineHeight: 1.2,
      },
    },
    headlineAfterSubheadline: {
      paddingTop: "0 !important",
      marginTop: 0,
    },
    body: {
      color: TEXT_BODY,
      fontSize: 18,
      lineHeight: 1.55,
      "& p:last-child": {
        marginBottom: 0,
      },
      "& strong": {
        color: TEXT_HEADING,
        fontWeight: 600,
      },
      [t.breakpoints.up("md")]: {
        fontSize: 20,
        lineHeight: 1.6,
      },
    },
    bodyCentered: {
      maxWidth: 800,
      margin: "0 auto",
    },
  })
);

const usePersonaStyles = makeStyles((t: Theme) =>
  createStyles({
    header: {
      marginBottom: "2.5rem",
      [t.breakpoints.up("md")]: {
        marginBottom: "3rem",
      },
    },
    card: {
      padding: "28px 24px",
      minHeight: 280,
      display: "flex",
      flexDirection: "column",
      height: "100%",
      [t.breakpoints.up("md")]: {
        padding: "32px 28px",
        minHeight: 320,
      },
    },
    cardAccent: {
      backgroundColor: t.palette.primary.main,
      color: "#fff",
    },
    cardMuted: {
      backgroundColor: "#eef0f3",
      color: TEXT_HEADING,
    },
    role: {
      fontWeight: 600,
      fontSize: 28,
      lineHeight: 1.2,
      marginBottom: 20,
      [t.breakpoints.up("md")]: {
        fontSize: 36,
      },
    },
    roleAccent: {
      color: "#fff",
    },
    roleMuted: {
      color: t.palette.primary.main,
    },
    question: {
      flex: 1,
      fontSize: 16,
      lineHeight: 1.5,
      marginBottom: 24,
      [t.breakpoints.up("md")]: {
        fontSize: 17,
      },
    },
    questionAccent: {
      color: "rgba(255, 255, 255, 0.95)",
    },
    questionMuted: {
      color: TEXT_BODY,
    },
    solutionLink: {
      fontSize: 15,
      fontWeight: 600,
      textDecoration: "underline",
      marginTop: "auto",
    },
    linkAccent: {
      color: "#fff",
    },
    linkMuted: {
      color: t.palette.primary.main,
    },
    gridItem: {
      padding: "0 !important" as unknown as number,
    },
  })
);

const ContentBlock: React.FC<{
  data: ContentBlockData;
  centered?: boolean;
  className?: string;
}> = ({ data, centered, className }) => {
  const labelClasses = useLabelStyles();
  if (!hasText(data.headline) && !hasText(data.description)) {
    return null;
  }

  return (
    <div className={[centered ? labelClasses.bodyCentered : "", className].filter(Boolean).join(" ")}>
      {hasText(data.subheadline) && (
        <Typography component="p" className={labelClasses.subheadline}>
          {data.subheadline}
        </Typography>
      )}
      {hasText(data.headline) && (
        <Typography
          variant="h2"
          component="h2"
          className={[
            labelClasses.headline,
            hasText(data.subheadline) && labelClasses.headlineAfterSubheadline,
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {data.headline}
        </Typography>
      )}
      {hasText(data.description) && (
        <div className={labelClasses.body}>
          <ReactMarkdown linkTarget="_blank" components={markdownComponents}>
            {data.description!}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
};

export const HowWeSolveSection: React.FC<HowWeSolveSectionProps> = ({ howWeSolve }) => {
  const classes = useStyles();
  const labelClasses = useLabelStyles();
  const showSolution =
    howWeSolve && (hasText(howWeSolve.headline) || hasText(howWeSolve.description));

  if (!showSolution) {
    return null;
  }

  return (
    <Section color={SECTION_WHITE} className={classes.section}>
      <Grid container spacing={4} alignItems="flex-start">
        <Grid item xs={12} md={5}>
          <ContentBlock
            data={{
              subheadline: howWeSolve!.subheadline,
              headline: howWeSolve!.headline,
              description: null,
            }}
          />
        </Grid>
        <Grid item xs={12} md={7}>
          {hasText(howWeSolve!.description) && (
            <div className={labelClasses.body}>
              <ReactMarkdown linkTarget="_blank" components={markdownComponents}>
                {howWeSolve!.description!}
              </ReactMarkdown>
            </div>
          )}
        </Grid>
      </Grid>
    </Section>
  );
};

const ProblemDefinitionSection: React.FC<ProblemDefinitionSectionProps> = ({
  locale,
  businessProblem,
  personaQuestions,
}) => {
  const classes = useStyles();
  const personaClasses = usePersonaStyles();

  const questions = personaQuestions?.questions?.filter((q) => q?.role && q?.question) ?? [];
  const showBusiness = businessProblem && (hasText(businessProblem.headline) || hasText(businessProblem.description));
  const showPersonas = personaQuestions && (hasText(personaQuestions.headline) || questions.length > 0);

  if (!showBusiness && !showPersonas) {
    return null;
  }

  return (
    <Section color={SECTION_TINT} className={classes.section}>
      <div className={classes.blocks}>
      {showBusiness && (
        <div className={classes.centered}>
          <ContentBlock data={businessProblem!} centered />
        </div>
      )}

      {showPersonas && (
        <div>
          <div className={personaClasses.header}>
            <ContentBlock
              data={{
                subheadline: personaQuestions!.subheadline,
                headline: personaQuestions!.headline,
                description: null,
              }}
            />
          </div>
          {questions.length > 0 && (
            <Grid container>
              {questions.map((item, index) => {
                const accent = index % 2 === 0;
                return (
                  <Grid item xs={12} sm={6} md={3} key={`${item.role}-${index}`} className={personaClasses.gridItem}>
                    <div
                      className={[
                        personaClasses.card,
                        accent ? personaClasses.cardAccent : personaClasses.cardMuted,
                      ].join(" ")}
                    >
                      <Typography
                        component="p"
                        className={[
                          personaClasses.role,
                          accent ? personaClasses.roleAccent : personaClasses.roleMuted,
                        ].join(" ")}
                      >
                        {item.role}
                      </Typography>
                      <Typography
                        component="p"
                        className={[
                          personaClasses.question,
                          accent ? personaClasses.questionAccent : personaClasses.questionMuted,
                        ].join(" ")}
                      >
                        {item.question}
                      </Typography>
                      {item.solutionLink?.text && item.solutionLink?.link && (
                        <CustomLink
                          link={item.solutionLink.link}
                          text={item.solutionLink.text}
                          anchor={item.solutionLink.anchor}
                          locale={locale}
                          className={[
                            personaClasses.solutionLink,
                            accent ? personaClasses.linkAccent : personaClasses.linkMuted,
                          ].join(" ")}
                        />
                      )}
                    </div>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </div>
      )}
      </div>
    </Section>
  );
};

export default ProblemDefinitionSection;
